import type { DocumentData, QuerySnapshot } from 'firebase/firestore'
import { onSnapshot, query, where } from 'firebase/firestore'

import type { Org } from '#shared/types/Org'
import type {
    OrgTransaction,
    OrgTransactionBalance,
    OrgTransactionPaymentEntityType,
    OrgTransactionWithAuthor,
    OrgWithTransactionBalance
} from '#shared/types/OrgTransaction'

/**
 * Composable for organization transactions functionality
 */
export function useOrgTransactions() {
    const { $firebase } = useNuxtApp()

    /**
     * Get organization transactions balance
     */
    const getOrgTransactionsBalance = async (orgId: OrgTransaction['orgId']): Promise<OrgTransactionBalance> => {
        return await $fetch(`/api/orgs/${orgId}/transactions/balance`)
    }

    /**
     * Get organization transactions
     */
    const getOrgTransactions = async (orgId: OrgTransaction['orgId']): Promise<OrgTransactionWithAuthor[]> => {
        return await $fetch(`/api/orgs/${orgId}/transactions`)
    }

    /**
     * Subscribe to transactions
     */
    const subscribeToTransactions = (
        orgId: OrgTransaction['orgId'],
        onNext: (snapshot: QuerySnapshot<DocumentData>) => void
    ): () => void => {
        const orgTransactionsQuery = query($firebase.collections.orgTransactions, where('orgId', '==', orgId))
        return onSnapshot(orgTransactionsQuery, onNext)
    }

    /**
     * Get organizations with balance
     */
    const getOrgsWithBalance = async (type?: OrgTransactionPaymentEntityType): Promise<OrgWithTransactionBalance[]> => {
        return await $fetch('/api/orgs/balance', {
            params: { type }
        })
    }

    /**
     * Get organization with balance
     */
    const getOrgWithBalance = async (
        orgSlug: Org['slug'],
        type?: OrgTransactionPaymentEntityType
    ): Promise<OrgWithTransactionBalance> => {
        return await $fetch(`/api/orgs/${orgSlug}/balance`, {
            params: { type }
        })
    }

    return {
        getOrgTransactionsBalance,
        getOrgTransactions,
        subscribeToTransactions,
        getOrgsWithBalance,
        getOrgWithBalance
    }
}
